/**
 * Utility functions for date formatting and manipulation
 */
import { format } from 'date-fns'
import { toDate } from '~/shared/utils/dates.js'

// Type definition for Firestore timestamp-like objects
interface TimestampLike {
  toDate(): Date
}

/**
 * A calendar day as local midnight. A string starting 'YYYY-MM-DD' (a date
 * input's value, or the date part of an ISO time) is that day:
 * `new Date('YYYY-MM-DD')` would be UTC midnight, which is the evening before
 * in the Americas. Anything else is read as a time, on its local day.
 */
function toCalendarDate(value: unknown): Date | null {
  if (typeof value === 'string') {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
    if (match) return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  }
  const date = toDate(value)
  return date ? new Date(date.getFullYear(), date.getMonth(), date.getDate()) : null
}

/**
 * Format a date for display with options
 * @param date Date to format - can be Date object, string, Firestore timestamp, or null
 * @param formatOptions Custom formatting options
 * @returns Formatted date string or empty string if date is null/undefined
 */
export function formatDate(date: Date | string | number | TimestampLike | null | undefined, formatOptions?: {
  showTime?: boolean
  format?: string
  locale?: Intl.LocalesArgument
}) {
  if (!date) return ''

  // For string dates, if it's already in YYYY-MM-DD format, parse it directly
  if (typeof date === 'string' && date.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const [year, month, day] = date.split('-').map(Number)
    // Use Date.UTC to avoid timezone issues - ensure all params are numbers
    const utcDate = new Date(Date.UTC(year || 0, (month || 0) - 1, day || 0))

    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: formatOptions?.showTime ? 'short' : 'long',
      day: 'numeric',
      timeZone: 'UTC', // Force UTC to avoid date shifting
    }

    // Add time if requested
    if (formatOptions?.showTime) {
      options.hour = '2-digit'
      options.minute = '2-digit'
    }

    return new Intl.DateTimeFormat(formatOptions?.locale || 'en-US', options).format(utcDate)
  }

  // For Date objects or Timestamps, create a new date to avoid mutation
  const dateObj = typeof date === 'object' && date !== null && 'toDate' in date && typeof date.toDate === 'function'
    ? new Date((date as TimestampLike).toDate().getTime())
    : date instanceof Date
      ? new Date(date.getTime())
      : new Date(date as string | number)

  // Extract year, month, day to create a "clean" date without time component
  const year = dateObj.getFullYear()
  const month = dateObj.getMonth()
  const day = dateObj.getDate()

  // Create a new UTC date to avoid timezone issues for display
  const displayDate = new Date(Date.UTC(year, month, day))

  // Format options
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: formatOptions?.showTime ? 'short' : 'long',
    day: 'numeric',
    timeZone: 'UTC', // Force UTC to prevent timezone shifts
  }

  // Add time if requested
  if (formatOptions?.showTime) {
    options.hour = '2-digit'
    options.minute = '2-digit'

    // For time, use the original date's time values
    displayDate.setUTCHours(dateObj.getHours())
    displayDate.setUTCMinutes(dateObj.getMinutes())
  }

  // If a specific format is provided using date-fns, use that instead.
  // Use the original local date — date-fns formats in local time, so passing
  // the UTC-shifted displayDate would render the previous day in negative
  // UTC offsets.
  if (formatOptions?.format) {
    return format(dateObj, formatOptions.format)
  }

  // Otherwise use Intl.DateTimeFormat
  return new Intl.DateTimeFormat(formatOptions?.locale || 'en-US', options).format(displayDate)
}

/**
 * Format a date to show only the date portion (Month Day, Year)
 * @param date The date to format
 * @returns Formatted date string
 */
export function formatDateOnly(date: Date | string | number | TimestampLike | null | undefined) {
  return formatDate(date)
}

/**
 * Format a date to include time (Month Day, Year, HH:MM)
 * @param date The date to format
 * @returns Formatted date string with time
 */
export function formatDateWithTime(date: Date | string | number | TimestampLike | null | undefined) {
  return formatDate(date, { showTime: true })
}

/**
 * Format a date with date and time (May 13, 2025, 15:30)
 * @param date The date to format
 * @returns Formatted date and time string
 */
export function formatDateTime(date: Date | string | number | TimestampLike | null | undefined) {
  if (!date) return ''

  const dateObj = typeof date === 'object' && date !== null && 'toDate' in date
    ? (date as TimestampLike).toDate()
    : date instanceof Date
      ? date
      : new Date(date as string | number)

  return format(dateObj, 'MMM d, yyyy, HH:mm')
}

/**
 * Check if a date is in the past (overdue)
 * @param date The date to check
 * @returns True if the date is in the past
 */
export function isDateOverdue(date: Date | string | number | TimestampLike | null | undefined) {
  if (!date) return false

  const dateObj = typeof date === 'object' && date !== null && 'toDate' in date
    ? (date as TimestampLike).toDate()
    : date instanceof Date
      ? date
      : new Date(date as string | number)

  return dateObj < new Date()
}

/**
 * Format a date in a compact format (MM/DD/YYYY)
 * @param date The date to format
 * @returns Short formatted date string
 */
export function formatShortDate(date: Date | string | number | TimestampLike | null | undefined) {
  return formatDate(date, { format: 'MM/dd/yyyy' })
}

/**
 * Format a phone number in standard format
 * @param phoneNumber Phone number to format
 * @returns Formatted phone number or original value if invalid
 */
export function formatPhoneNumber(phoneNumber: string | null | undefined): string {
  if (!phoneNumber) return ''

  // Basic US phone number formatting
  const cleaned = phoneNumber.toString().replace(/\D/g, '')

  if (cleaned.length === 10) {
    return `(${cleaned.substring(0, 3)}) ${cleaned.substring(3, 6)}-${cleaned.substring(6)}`
  }

  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+1 (${cleaned.substring(1, 4)}) ${cleaned.substring(4, 7)}-${cleaned.substring(7)}`
  }

  return phoneNumber // Return original if we can't format it
}

/**
 * Get a date that is 18 years ago from today
 * @returns Date object representing 18 years ago
 */
export function get18YearsAgoDate(): Date {
  const date = new Date()
  date.setFullYear(date.getFullYear() - 18)
  return date
}

/**
 * Get a date that is 100 years ago from today (for date input min value)
 * @returns Date object representing 100 years ago
 */
export function get100YearsAgoDate(): Date {
  const date = new Date()
  date.setFullYear(date.getFullYear() - 100)
  return date
}

/**
 * Get a date that is 18 years ago plus one day from today (for validation)
 * @returns Date object representing 18 years ago plus one day
 */
export function get18YearsAgoPlusOneDay(): Date {
  const date = new Date()
  date.setFullYear(date.getFullYear() - 18)
  date.setDate(date.getDate() + 1)
  return date
}

/**
 * Format a date as YYYY-MM-DD for HTML input type="date"
 * @param date Date to format
 * @returns Date string in YYYY-MM-DD format
 */
export function formatDateForInput(date: Date | string | number | TimestampLike | null | undefined): string {
  // The local day: toISOString() gave tomorrow's date after 7pm in Chicago
  const day = toCalendarDate(date)
  return day ? format(day, 'yyyy-MM-dd') : ''
}

/**
 * Check if someone is at least 18 years old based on their birthdate
 * @param birthdate The birthdate to check
 * @returns True if the person is at least 18 years old
 */
export function isAtLeast18(birthdate: Date | string | number | TimestampLike | null | undefined): boolean {
  const born = toCalendarDate(birthdate)
  if (!born) return false

  // By local calendar day: 18 from the morning of the 18th birthday
  const today = new Date()
  const birthdayToCome = today.getMonth() < born.getMonth()
    || (today.getMonth() === born.getMonth() && today.getDate() < born.getDate())
  return today.getFullYear() - born.getFullYear() - (birthdayToCome ? 1 : 0) >= 18
}

/**
 * Check if a date is valid
 * @param date The date string to validate
 * @returns True if the date is valid
 */
export function isValidDate(date: string | null | undefined): boolean {
  if (!date) return false

  const dateObj = new Date(date)
  return !isNaN(dateObj.getTime())
}
